import React, { useMemo, useState } from 'react';
import { Modal, Popover } from 'antd';
import classNames from 'classnames';
import { DotsIcon } from '@/utils/icons';
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper';
import s from './ChatTopics.module.css';

const ChatTopics = ({
  topics = [],
  activeTopicId = null,
  isLoading = false,
  isAdmin = false,
  className = '',
  onSelectTopic,
  onCreateTopic,
  onRenameTopic,
  onDeleteTopic,
}) => {
  const [isCreatingTopic, setIsCreatingTopic] = useState(false);
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [editingTopicId, setEditingTopicId] = useState(undefined);
  const [editingTopicTitle, setEditingTopicTitle] = useState('');
  const [activeMenuTopicId, setActiveMenuTopicId] = useState(null);
  const [pendingTopicId, setPendingTopicId] = useState(null);

  const hasTopics = topics.length > 0;

  const topicsCountMap = useMemo(() => {
    return new Map(
      topics.map((topic) => [String(topic.id), Number(topic?.unreadCount ?? 0)]),
    );
  }, [topics]);

  const handleStartCreate = () => {
    setIsCreatingTopic(true);
    setNewTopicTitle('');
    setEditingTopicId(undefined);
    setActiveMenuTopicId(null);
  };

  const handleCancelCreate = () => {
    setIsCreatingTopic(false);
    setNewTopicTitle('');
  };

  const handleCreateTopic = async () => {
    const preparedTitle = newTopicTitle.trim();
    if (!preparedTitle || pendingTopicId === 'create') return;

    setPendingTopicId('create');

    try {
      await onCreateTopic?.(preparedTitle);
      handleCancelCreate();
    } finally {
      setPendingTopicId(null);
    }
  };

  const handleStartRename = (topic) => {
    setEditingTopicId(topic.id);
    setEditingTopicTitle(topic.title ?? '');
    setActiveMenuTopicId(null);
    setIsCreatingTopic(false);
  };

  const handleCancelRename = () => {
    setEditingTopicId(undefined);
    setEditingTopicTitle('');
  };

  const handleRenameTopic = async () => {
    const preparedTitle = editingTopicTitle.trim();
    if (!editingTopicId || !preparedTitle || pendingTopicId === editingTopicId) return;

    setPendingTopicId(editingTopicId);

    try {
      await onRenameTopic?.({
        id: editingTopicId,
        title: preparedTitle,
      });
      handleCancelRename();
    } finally {
      setPendingTopicId(null);
    }
  };

  const handleDeleteTopic = (topic) => {
    setActiveMenuTopicId(null);

    Modal.confirm({
      className: s.confirm,
      title: 'Удалить тему?',
      content: `Тема "${topic.title}" и все сообщения внутри нее будут удалены.`,
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk: async () => {
        setPendingTopicId(topic.id);

        try {
          await onDeleteTopic?.(topic.id);
        } finally {
          setPendingTopicId(null);
        }
      },
    });
  };

  if (!hasTopics && !isAdmin) return null;

  return (
    <div className={classNames(s.wrapper, className)}>
      <div className={classNames('new-design-scroll', s.list)}>
        {isAdmin && !isCreatingTopic && (
          <TooltipWrapper
            placement="top"
            title={hasTopics ? 'Создать тему' : 'Создать первую тему'}>
            <button
              type="button"
              className={s.createButton}
              onClick={handleStartCreate}>
              <span className={s.createButtonIcon}>+</span>
              <span>Тема</span>
            </button>
          </TooltipWrapper>
        )}
        {isCreatingTopic && (
          <div className={classNames(s.topicItem, s.topicItemEditor)}>
            <div className={s.editor}>
              <input
                value={newTopicTitle}
                onChange={(event) => setNewTopicTitle(event.target.value)}
                className={s.input}
                placeholder="Новая тема"
                autoFocus
              />
              <button
                type="button"
                className={s.actionButton}
                disabled={pendingTopicId === 'create'}
                onClick={handleCreateTopic}>
                Создать
              </button>
              <button
                type="button"
                className={classNames(s.actionButton, s.secondaryButton)}
                onClick={handleCancelCreate}>
                Отмена
              </button>
            </div>
          </div>
        )}

        {topics.map((topic) => {
          const isDefaultTopic = topic?.isDefault;
          const isActive = String(activeTopicId) === String(topic.id);
          const isEditing =
            editingTopicId !== null &&
            editingTopicId !== undefined &&
            String(editingTopicId) === String(topic.id);
          const isPending = String(pendingTopicId) === String(topic.id);
          const messagesCount = isDefaultTopic ? null : topicsCountMap.get(String(topic.id));

          return (
            <div
              key={topic.id === null ? 'default-topic' : topic.id}
              className={classNames(s.topicItem, {
                [s.topicItemActive]: isActive,
              })}>
              {isEditing ? (
                <div className={s.editor}>
                  <input
                    value={editingTopicTitle}
                    onChange={(event) => setEditingTopicTitle(event.target.value)}
                    className={s.input}
                    placeholder="Название темы"
                    autoFocus
                  />
                  <button
                    type="button"
                    className={s.actionButton}
                    disabled={isPending}
                    onClick={handleRenameTopic}>
                    Сохранить
                  </button>
                  <button
                    type="button"
                    className={classNames(s.actionButton, s.secondaryButton)}
                    onClick={handleCancelRename}>
                    Отмена
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className={s.topicButton}
                    disabled={isLoading}
                    onClick={() => onSelectTopic?.(topic.id)}>
                    <span className={s.topicTitle}>{topic.title}</span>
                    {messagesCount !== null && (
                      <span className={s.topicCount}>{messagesCount}</span>
                    )}
                  </button>

                  {!isDefaultTopic && isAdmin && (
                    <div className={s.topicActions}>
                      <Popover
                        trigger="click"
                        placement="bottomRight"
                        open={activeMenuTopicId === topic.id}
                        onOpenChange={(isOpen) => setActiveMenuTopicId(isOpen ? topic.id : null)}
                        getPopupContainer={() => document.body}
                        content={(
                          <div className={s.menuPopover}>
                            <button
                              type="button"
                              className={s.menuItem}
                              onClick={() => handleStartRename(topic)}>
                              <span>Переименовать</span>
                            </button>
                            <button
                              type="button"
                              className={classNames(s.menuItem, s.menuItemDanger)}
                              onClick={() => handleDeleteTopic(topic)}>
                              <span>Удалить</span>
                            </button>
                          </div>
                        )}>
                        <button
                          type="button"
                          className={s.iconButton}>
                          <DotsIcon />
                        </button>
                      </Popover>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChatTopics;
